import { ApiError, api } from '../../lib/api'
import { toFormData } from '../../lib/form'

/**
 * Exams — `Features/Admin/ManageExams/Endpoint.cs`, `ContentManagers`.
 *
 *   listExams        -> POST /admin/manageexams/getexamslist
 *   getExam          -> POST /admin/manageexams/getdatarow            form: examId
 *   getExamOptions   -> POST /admin/manageexams/addprerequisites
 *   optionsForCourse -> POST /admin/manageexams/getselectedcoursechanged    form: courseId
 *   optionsForTopic  -> POST /admin/manageexams/getselectedcategorychanged  form: categoryId
 *   questionsAtLevel -> POST /admin/manageexams/getselectedlevelchanged     form: categoryId, levelId
 *   saveExam         -> POST /admin/manageexams/addexam | /editexam
 *   deleteExam       -> POST /admin/manageexams/deleteexam             form: examId
 *
 * WHAT THIS SCREEN IS FOR, given the course form already has exam settings.
 *
 * A course's exam has a pass mark, a number of tries and three switches, and the course
 * form writes those through `SaveCourse.ExamDetails`. What it CANNOT express is how the
 * exam is composed: RULES of the form "five questions from this topic at this difficulty".
 * That is `ExamRuleDto`, and it only exists here.
 *
 * THE OPTION LISTS CASCADE, and each step is a call because each depends on data only the
 * server has: which topics a course covers, which difficulties a topic has questions at,
 * and HOW MANY questions exist at that pairing — the cap on a rule. Asking for six
 * questions where four exist is the mistake this prevents.
 *
 * Everything is form-encoded, and every reply is
 * `{ Result, Message, DataSet2 }` — the payload is under `DataSet2`.
 */

const BASE = '/admin/manageexams'

/** `{ Result, Message, DataSet2 }` — unwrapped, or thrown as an ApiError. */
async function post(path, fields, signal) {
  const data = await api.post(`${BASE}/${path}`, toFormData(fields ?? {}), { signal })
  return data?.DataSet2 ?? null
}

const options = (list) =>
  (list ?? []).map((option) => ({ value: option.Value, label: option.DisplayField ?? '' }))

export async function listExams(signal) {
  // This endpoint returns a bare JSON array, unlike the other ManageExams actions
  // that wrap their payload in DataSet2. Unwrapping it through post() hid every exam.
  const response = await api.post(`${BASE}/getexamslist`, toFormData({}), { signal })
  const rows = Array.isArray(response) ? response : (response?.DataSet2 ?? [])

  return {
    rows: (Array.isArray(rows) ? rows : (rows.Exams ?? [])).map((dto) => ({
      id: dto.Id,
      course: dto.CourseName ?? '',
      name: dto.ExamName ?? '',
    })),
  }
}

/**
 * The lists a NEW exam starts from: every course without one yet, and the topics.
 *
 * "Every course already has an exam" is not an error, but it arrives as one: the handler
 * answers `Result: false` with a message and NO `DataSet2`, which `api.js` turns into an
 * `ApiError`. It is read back here as a state so the screen can say the server's own
 * sentence rather than a generic failure.
 */
export async function getExamOptions(signal) {
  try {
    return toSettings(await post('addprerequisites', {}, signal))
  } catch (error) {
    if (error instanceof ApiError && error.code === 'RESULT') {
      return { ...EMPTY_SETTINGS, allCoursesHaveExam: true, message: error.message }
    }
    throw error
  }
}

/** What `toSettings` answers for a payload that never arrived. */
const EMPTY_SETTINGS = {
  exam: null,
  allCoursesHaveExam: false,
  courses: [],
  topics: [],
  levels: [],
  questionsAvailable: 0,
  questionCounts: [],
  rules: [],
  message: '',
}

/** One exam with its rules, and the option lists positioned for it. */
export async function getExam(examId, signal) {
  return toSettings(await post('getdatarow', { examId: String(examId) }, signal))
}

/** The topics a course covers. */
export async function optionsForCourse(courseId, signal) {
  return toSettings(await post('getselectedcoursechanged', { courseId: String(courseId) }, signal))
}

/** The difficulties a topic has questions at. */
export async function optionsForTopic(categoryId, signal) {
  return toSettings(await post('getselectedcategorychanged', { categoryId: String(categoryId) }, signal))
}

/**
 * How many questions exist at one topic-and-difficulty pairing — the ceiling for a rule.
 */
export async function questionsAtLevel(categoryId, levelId, signal) {
  return toSettings(
    await post('getselectedlevelchanged', { categoryId: String(categoryId), levelId: String(levelId) }, signal),
  )
}

/** `ExamSettingsDto` — the same shape from all five reads above. */
function toSettings(dto) {
  if (!dto) return null

  const exam = dto.Exam

  return {
    exam: exam
      ? {
          id: exam.Id,
          courseId: exam.CourseId || dto.CoursesData?.[0]?.Value,
          nameAr: exam.ExamNameAr ?? '',
          nameEn: exam.ExamNameEn ?? '',
          passLimit: exam.PassLimit ?? 0,
          tries: exam.TriesCount ?? 0,
          forceWatch: Boolean(exam.ForceWatch),
          hideAnswers: Boolean(exam.HideAnswers),
          preventIfSucceeded: Boolean(exam.PreventIfSucceeded),
          rewatchOnFailPercentage: exam.RewatchOnFailPercentage ?? null,
          /* A comma-separated string of rule ids, not a list — the shape the save takes
             back, so it is carried rather than rebuilt. */
          rulesIds: exam.RulesIds ?? '',
        }
      : null,
    /* Whether every course already has an exam, i.e. there is nothing left to create one
       for. The screen says so instead of offering an empty dropdown. */
    allCoursesHaveExam: Boolean(dto.AllCoursesHasExam),
    courses: options(dto.CoursesData),
    topics: options(dto.CategoriesData),
    levels: options(dto.LevelsData),
    /** How many questions the chosen topic/difficulty pairing has. */
    questionsAvailable: dto.LevelQuestionsCount ?? 0,
    questionCounts: options(dto.LevelQuestionCountData),
    rules: (dto.ExamRulesData ?? []).map((rule) => ({
      id: rule.Id,
      examId: rule.ExamId,
      topicId: rule.CategoryId || Number(String(rule.Id).split(',')[0]),
      topic: rule.CategoryName ?? '',
      levelId: rule.DifficultyId || Number(String(rule.Id).split(',')[1]),
      level: rule.DifficultyName ?? '',
      count: rule.NumberOfQuestion ?? 0,
    })),
  }
}

/**
 * Create or update.
 *
 * The fields are prefixed `data.` on the wire — the handler reads `form["data.CourseId"]`
 * literally, which is what the legacy form's nested model binding produced.
 */
export async function saveExam(values) {
  const fields = {
    'data.CourseId': String(values.courseId ?? 0),
    'data.ExamNameAr': values.nameAr ?? '',
    'data.ExamNameEn': values.nameEn ?? '',
    'data.PassLimit': String(values.passLimit ?? 0),
    'data.TriesCount': String(values.tries ?? 0),
    /* Compared against the string "true", so a boolean has to be written out. */
    'data.ForceWatch': values.forceWatch ? 'true' : 'false',
    'data.HideAnswers': values.hideAnswers ? 'true' : 'false',
    'data.PreventIfSucceeded': values.preventIfSucceeded ? 'true' : 'false',
    'data.RulesIds': values.rulesIds ?? '',
  }

  if (values.rewatchOnFailPercentage != null && values.rewatchOnFailPercentage !== '') {
    fields['data.RewatchOnFailPercentage'] = String(values.rewatchOnFailPercentage)
  }

  const isUpdate = Number(values.id ?? 0) > 0
  if (isUpdate) fields.examId = String(values.id)

  try {
    const data = await api.post(`${BASE}/${isUpdate ? 'editexam' : 'addexam'}`, toFormData(fields))
    return { ok: data?.Result !== false, message: data?.Message ?? '' }
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, message: error.message }
    throw error
  }
}

export async function deleteExam(examId) {
  try {
    const data = await api.post(`${BASE}/deleteexam`, toFormData({ examId: String(examId) }))
    return { ok: data?.Result !== false, message: data?.Message ?? '' }
  } catch (error) {
    if (error instanceof ApiError) return { ok: false, message: error.message }
    throw error
  }
}
